import React from 'react';
import { message } from 'antd';
import _flatten from 'lodash/flatten';
import { Controller, useController, useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { useSignedUser } from 'api/auth';
import { createStore } from 'api/store';
import { CountryCode } from 'api/types/store';
import { StoreSource, useAnalytics } from 'analytics';
import ZipCodeInput from 'components/Form/ZipCodeInput';
import { getFieldError } from 'components/NewProductStepper/utils';
import { URLS } from 'components/Routes/urls';
import { STORE_WALKTHROUGH } from 'components/Onboarding/StoreWalkthrough';
import { Button, PanelModal, TextField } from 'ui-kit';
import { defaultCountry } from 'utils/geo';
import { reverse } from 'utils/urls';
import './QuickSetupModal.scss';

const BASE_CLASS = 'QuickSetupModal';
const FORM_ID = 'quick-setup-form';
const SOURCE = StoreSource.onboarding;
const GENERIC_ERROR = 'Something went wrong. Please try again.';

const PRODUCTS = [
  {
    title: 'Early Check-In / Late-Check Out',
    badge: 'Best seller',
    image: `${process.env.PUBLIC_URL}/intro/f1.png`,
  },
  { title: 'Guest & Pet Fees', image: `${process.env.PUBLIC_URL}/intro/f2.png` },
  { title: 'Local Services', image: `${process.env.PUBLIC_URL}/intro/f3.png` },
];

interface FormValues {
  country: CountryCode;
  postalCode: string;
  storeName: string;
}

const required = (message: string) => ({
  validate: (value: string) => Boolean(value.trim()) || message,
});

export default function QuickSetupModal() {
  const history = useHistory();
  const capture = useAnalytics();
  const { data: user } = useSignedUser();

  const {
    control,
    handleSubmit,
    formState: { isValid, isSubmitting },
  } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: { country: defaultCountry as CountryCode, postalCode: '', storeName: '' },
  });
  const { field: countryField } = useController({ control, name: 'country' });

  const onSubmit = ({ country, postalCode, storeName }: FormValues) => {
    capture('store_create_submitted', { source: SOURCE });

    return createStore({
      owner: user.owner_id,
      country,
      postalCode: postalCode.trim(),
      storeName: storeName.trim(),
    })
      .then((res) => {
        if (res.ok) {
          capture(
            'store_created',
            { source: SOURCE, store_id: res.data?.id },
            { setOnce: { first_store_created_at: new Date().toISOString() } },
          );
          history.push(reverse({ url: URLS.STORE_DETAILS, params: { storeId: res.data.id } }), {
            startWalkthrough: STORE_WALKTHROUGH,
          });
          return;
        }
        // `errors.fields` is keyed by field name, not indexed.
        const fields = res.errors.fields ?? {};
        capture('store_create_failed', {
          source: SOURCE,
          reason: Object.keys(fields).join(',') || res.errors.nonFields?.[0] || 'unknown',
        });
        message.error(
          res.errors.nonFields?.[0] ?? _flatten(Object.values(fields))[0] ?? GENERIC_ERROR,
        );
      })
      .catch(() => {
        capture('store_create_failed', { source: SOURCE, reason: 'request_failed' });
        message.error(GENERIC_ERROR);
      });
  };

  return (
    <PanelModal
      visible
      closable={false}
      className={BASE_CLASS}
      title="You’re in! Let’s make you a successful store in three clicks."
      header={
        <form id={FORM_ID} className={`${BASE_CLASS}-form`} onSubmit={handleSubmit(onSubmit)}>
          <Controller
            control={control}
            name="postalCode"
            rules={required('ZIP code is required')}
            render={({ field: { ref, ...rest }, fieldState }) => (
              <ZipCodeInput
                label="ZIP code of your first listing"
                required
                fullWidth
                country={countryField.value}
                onCountryChange={countryField.onChange}
                inputRef={ref}
                {...getFieldError(fieldState)}
                {...rest}
              />
            )}
          />
          <Controller
            control={control}
            name="storeName"
            rules={required('Listing name is required')}
            render={({ field: { ref, ...rest }, fieldState }) => (
              <TextField
                label="Name of your listing"
                placeholder="e.g. Cozy Cabin"
                required
                fullWidth
                inputRef={ref}
                {...getFieldError(fieldState)}
                {...rest}
              />
            )}
          />
        </form>
      }
      footer={
        <Button
          variant="contained"
          type="submit"
          form={FORM_ID}
          className={`${BASE_CLASS}-submit`}
          disabled={!isValid}
          loading={isSubmitting}
        >
          Make my first store
        </Button>
      }
    >
      <div className={`${BASE_CLASS}-products`}>
        {PRODUCTS.map(({ title, badge, image }) => (
          <div key={title} className={`${BASE_CLASS}-product`}>
            <div
              className={`${BASE_CLASS}-product__image`}
              style={{ backgroundImage: `url(${image})` }}
            />
            {badge && <span className={`${BASE_CLASS}-product__badge`}>{badge}</span>}
            <p className={`${BASE_CLASS}-product__title`}>{title}</p>
          </div>
        ))}
      </div>
      <p className={`${BASE_CLASS}-note`}>You can turn products on or off at any time.</p>
    </PanelModal>
  );
}
